import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axiosInstance';

// Thunk to create a new product
export const createProduct = createAsyncThunk('product/createProduct', async (productData) => {
  const response = await axiosInstance.post('/product', productData);
  return response.data;
});

export const fetchProducts = createAsyncThunk('product/fetchProducts', async () => {
  const response = await axiosInstance.get('/product');
  return response.data;
});

export const deleteProduct = createAsyncThunk('product/deleteProduct', async (id) => {
  const response = await axiosInstance.delete(`/product/${id}`);
  return response.data;
});

export const fetchProduct = createAsyncThunk('product/fetchProduct', async (id) => {
  const response = await axiosInstance.get(`/product/${id}`);
  return response.data;
});

export const updateProduct = createAsyncThunk('product/updateProduct', async (productData) => {
  const response = await axiosInstance.put(`/product/${productData.id}`, productData.formData);
  return response.data;
});

export const searchProducts = createAsyncThunk('product/searchProducts', async (val) => {
  const response = await axiosInstance.get('/product/products_data', { params: { searchTerm: val } })
  return response.data;
});

export const bulkUploadProducts = createAsyncThunk(
  'product/bulkUploadProducts', 
  async (productData, { rejectWithValue }) => {
    try {
      // Use a 10-minute timeout for bulk uploads of large files
      const response = await axiosInstance.post('/product/bulk-upload-products', productData, {
        timeout: 600000  // 10 minutes for bulk uploads
      });
      return response.data;
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        return rejectWithValue(error.response.data.error);
      }
      return rejectWithValue(error.message || 'Failed to upload products');
    }
  }
);

export const bulkUpdateInventory = createAsyncThunk(
  'product/bulkUpdateInventory',
  async (inventoryUpdates, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/product/bulk-update-inventory', { inventoryUpdates });
      return response.data;
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        return rejectWithValue(error.response.data.message);
      }
      return rejectWithValue(error.message || 'Failed to update inventory');
    }
  }
);

const productSlice = createSlice({
  name: 'product',
  initialState: {
    products: [],
    status: 'idle',
    error: null,
  },
  reducers: {
    clearProducts: (state) => {
      state.products = [];
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createProduct.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.products.push(action.payload);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchProducts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(deleteProduct.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.products = action.payload;
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchProduct.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchProduct.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(fetchProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(updateProduct.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.products.findIndex(product => product._id === action.payload._id);
        if (index !== -1) {
          state.products[index] = action.payload;
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(searchProducts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.products = action.payload;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(bulkUploadProducts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(bulkUploadProducts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Refresh products after bulk upload
        state.products = action.payload;
      })
      .addCase(bulkUploadProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(bulkUpdateInventory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(bulkUpdateInventory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Refresh products after bulk inventory update
        state.products = action.payload;
      })
      .addCase(bulkUpdateInventory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Listen for store changes and clear products
      .addMatcher(
        (action) => action.type === 'store/setSelectedStore' || action.type === 'store/clearStoreData',
        (state, action) => {
          // Clear products when store changes
          state.products = [];
          state.status = 'idle';
          state.error = null;
        }
      );
  },
});

export const { clearProducts } = productSlice.actions;
export default productSlice.reducer;
